import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Refresh, ToggleOff, ToggleOn } from '@mui/icons-material';
import { platformApi, User } from '../services/platformApi';
import { getStatusColor, languageOptions } from './projectConstants';
import { useLanguage } from '../LanguageContext';
import { formatTranslatedLabel } from '../i18n';

interface UserFormState {
  email: string;
  username: string;
  displayName: string;
  phoneNumber: string;
  password: string;
  isAdmin: boolean;
  preferredUiLanguage: string;
}

const initialFormState: UserFormState = {
  email: '',
  username: '',
  displayName: '',
  phoneNumber: '',
  password: '',
  isAdmin: false,
  preferredUiLanguage: 'en',
};

const languageLabel = (value: string | null | undefined, t: (key: string) => string): string =>
  languageOptions.find((option) => option.value === value)?.label || value || t('notSet');

const getRequestErrorMessage = (err: unknown, fallback: string): string => {
  const responseData =
    typeof err === 'object' && err !== null && 'response' in err
      ? (err as { response?: { data?: { detail?: unknown; message?: unknown } } }).response?.data
      : undefined;

  if (typeof responseData?.detail === 'string') {
    return responseData.detail;
  }
  const detail = responseData?.detail;
  if (Array.isArray(detail)) {
    const firstMessage = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (
          typeof item === 'object' &&
          item !== null &&
          'msg' in item &&
          typeof (item as { msg?: unknown }).msg === 'string'
        ) {
          return (item as { msg: string }).msg;
        }
        return null;
      })
      .find(Boolean);
    if (firstMessage) {
      return firstMessage;
    }
  }
  if (typeof responseData?.message === 'string') {
    return responseData.message;
  }
  return fallback;
};

const UserManagement: React.FC = () => {
  const { language, t } = useLanguage();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState<UserFormState>(initialFormState);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setUsers(await platformApi.listUsers());
    } catch (err) {
      setError(t('couldNotLoadUsers'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async () => {
    if (!formState.email.trim()) {
      setDialogError(t('emailRequired'));
      setSuccess(null);
      return;
    }
    if (formState.password && formState.password.length < 8) {
      setDialogError(t('temporaryPasswordMinLength'));
      setSuccess(null);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setDialogError(null);
      const createdUser = await platformApi.createUser({
        email: formState.email.trim(),
        username: formState.username.trim() || undefined,
        display_name: formState.displayName.trim() || undefined,
        phone_number: formState.phoneNumber.trim() || undefined,
        password: formState.password || undefined,
        is_admin: formState.isAdmin,
        preferred_ui_language: formState.preferredUiLanguage,
      });
      setDialogOpen(false);
      setFormState(initialFormState);
      await loadUsers();
      setSuccess(`${t('createdUser')} ${createdUser.display_name || createdUser.email}.`);
    } catch (err) {
      setDialogError(getRequestErrorMessage(err, t('couldNotCreateUser')));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    const nextStatus = user.status === 'active' ? 'deactivated' : 'active';
    try {
      setError(null);
      setSuccess(null);
      await platformApi.updateUserStatus(user.id, nextStatus);
      await loadUsers();
    } catch (err) {
      setError(t('couldNotUpdateUserStatus'));
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('usersAndAccess')}
          </Typography>
          <Typography color="text.secondary">
            {t('usersAndAccessDescription')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton aria-label={t('refreshUsers')} onClick={loadUsers}>
            <Refresh />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setDialogError(null);
              setFormState({ ...initialFormState, preferredUiLanguage: language });
              setDialogOpen(true);
            }}
          >
            {t('newUser')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('user')}</TableCell>
              <TableCell>{t('username')}</TableCell>
              <TableCell>{t('uiLanguage')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('scope')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Typography fontWeight={700}>{user.display_name || user.email}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell>{user.username || t('notSet')}</TableCell>
                <TableCell>{languageLabel(user.preferred_ui_language, t)}</TableCell>
                <TableCell>
                  <Chip size="small" label={formatTranslatedLabel(t, user.status)} color={getStatusColor(user.status)} />
                </TableCell>
                <TableCell>{user.is_admin ? t('admin') : t('projectScoped')}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label={user.status === 'active' ? t('deactivateUser') : t('activateUser')}
                    onClick={() => handleToggleStatus(user)}
                    color={user.status === 'active' ? 'warning' : 'success'}
                  >
                    {user.status === 'active' ? <ToggleOff /> : <ToggleOn />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
                    {t('noUsersYet')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('createUser')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <TextField
              label={t('email')}
              type="email"
              value={formState.email}
              onChange={(event) => {
                setDialogError(null);
                setFormState({ ...formState, email: event.target.value });
              }}
              fullWidth
              required
              error={Boolean(dialogError && !formState.email.trim())}
              helperText={dialogError && !formState.email.trim() ? t('enterEmailAddress') : undefined}
            />
            <TextField
              label={t('displayName')}
              value={formState.displayName}
              onChange={(event) => setFormState({ ...formState, displayName: event.target.value })}
              fullWidth
            />
            <TextField
              label={t('username')}
              value={formState.username}
              onChange={(event) => setFormState({ ...formState, username: event.target.value })}
              fullWidth
            />
            <TextField
              label={t('phoneNumber')}
              value={formState.phoneNumber}
              onChange={(event) => setFormState({ ...formState, phoneNumber: event.target.value })}
              fullWidth
            />
            <TextField
              label={t('temporaryPassword')}
              type="password"
              value={formState.password}
              onChange={(event) => {
                setDialogError(null);
                setFormState({ ...formState, password: event.target.value });
              }}
              fullWidth
              error={Boolean(dialogError && formState.password.length > 0 && formState.password.length < 8)}
              helperText={t('temporaryPasswordHelper')}
            />
            <FormControl fullWidth>
              <InputLabel id="user-ui-language-label">{t('uiLanguage')}</InputLabel>
              <Select
                labelId="user-ui-language-label"
                label={t('uiLanguage')}
                value={formState.preferredUiLanguage}
                onChange={(event) => setFormState({ ...formState, preferredUiLanguage: event.target.value })}
              >
                {languageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formState.isAdmin}
                  onChange={(event) => setFormState({ ...formState, isAdmin: event.target.checked })}
                />
              }
              label={t('admin')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleCreateUser} disabled={saving}>
            {saving ? t('creating') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
